import React from 'react';

export const PICK_SCAN_BAR_VARIANTS = {
  CAMERA: 'camera',
  SUBMIT: 'submit',
  NAV: 'nav',
} as const;

export type PickScanBarVariant =
  (typeof PICK_SCAN_BAR_VARIANTS)[keyof typeof PICK_SCAN_BAR_VARIANTS];

export interface PickScanBarProps {
  open: boolean;
  onShutter?: () => void;
  /** Shutter stays visible and does not capture. */
  shutterDisabled?: boolean;
  shutterLabel?: string;
  picked?: boolean;
  barVariant?: PickScanBarVariant;
  leftActions?: React.ReactNode;
  /** Rendered in the center of the bar while the camera sheet is closed (the shutter owns that slot when open). */
  centerActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  completeActions?: React.ReactNode;
  /** Red shutter stays; the one-third scan sheet is not rendered. */
  suppressSheet?: boolean;
  /** Accessible name of the scan sheet dialog. */
  sheetLabel?: string;
  children?: React.ReactNode;
}

interface CameraIconProps {
  size?: number;
}

export function CameraIcon({ size = 32 }: CameraIconProps): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M2 8H4L6 5H18L20 8H22V19H2V8Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function PickScanBar({
  open,
  onShutter,
  shutterDisabled = false,
  shutterLabel = 'Take picture',
  picked = false,
  barVariant = PICK_SCAN_BAR_VARIANTS.CAMERA,
  leftActions,
  centerActions,
  rightActions,
  completeActions,
  suppressSheet = false,
  sheetLabel = 'Camera',
  children,
}: PickScanBarProps): React.ReactElement {
  const showShutterChrome = barVariant === PICK_SCAN_BAR_VARIANTS.CAMERA;
  const barClassName =
    barVariant === PICK_SCAN_BAR_VARIANTS.SUBMIT
      ? 'order-detail-bottom-bar order-detail-bottom-bar-submit'
      : barVariant === PICK_SCAN_BAR_VARIANTS.NAV
        ? 'order-detail-bottom-bar order-detail-bottom-bar-nav'
        : 'order-detail-bottom-bar';

  return (
    <>
      {open && !suppressSheet ? (
        <div
          className="order-detail-camera-sheet"
          role="dialog"
          aria-label={sheetLabel}
          onClick={shutterDisabled ? undefined : onShutter}
        >
          {children}
        </div>
      ) : null}
      {picked && !open && completeActions != null ? (
        <div className="order-detail-complete-sheet" role="region" aria-label="Pick complete">
          {completeActions}
        </div>
      ) : null}
      <div className={barClassName}>
        {barVariant === PICK_SCAN_BAR_VARIANTS.NAV ? (
          <>
            {leftActions}
            {centerActions}
            {rightActions}
          </>
        ) : (
          <>
            <div className="order-detail-bottom-bar-side order-detail-bottom-bar-left">
              {leftActions}
            </div>
            <div className="order-detail-bottom-bar-center">
              {!open ? centerActions : null}
            </div>
            <div className="order-detail-bottom-bar-side order-detail-bottom-bar-right">
              {rightActions}
            </div>
            {showShutterChrome ? (
              open ? (
                <button
                  type="button"
                  className="order-detail-camera-btn"
                  onClick={onShutter}
                  disabled={shutterDisabled}
                  aria-label={shutterLabel}
                />
              ) : (
                <div className="order-detail-camera-btn order-detail-camera-btn-spacer" aria-hidden="true" />
              )
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

export default PickScanBar;
